import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, ModuleFE_Toaster } from '@nu-art/thunderstorm-frontend';
import { TS_ListOrganizer } from '@nu-art/thunderstorm-frontend/components/TS_ListOrganizer/index';
import { _keys, _values, arrayIncludesAny, asArray, dbObjectToId, exists, filterInstances, MUSTNeverHappenException, reduceToMap, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Expression } from '@app/hcs-frontend';
import { Panel_ExpressionSidePanel } from '@app/hcs-frontend/expression';
import { Dialog_Confirmation, ICONS } from '@app/styles';
import { ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../../../../_entity.js';
import { getEmptyResolutionFunction } from '../../../../Component_ResolutionFunctionEditor/const.js';
import { Component_ResolutionFunctionCard } from '../../../../Component_ResolutionFunctionEditor/components/Component_ResolutionFunctionCard.js';
import { guardResolutionAccordionUnsaved, setResolutionAccordionDirty, isResolutionAccordionDirty } from '../shared/resolution-accordion-guard.js';
import { Dialog_DeletePathwayTransition } from '../../_dialogs/Dialog_DeletePathwayTransition/Dialog_DeletePathwayTransition.js';
import './Panel_PathwayResolutionAccordion.scss';
// Covers the map column with an accordion of ALL the state's outgoing resolution functions
// (one collapsible card per transition, including transitions that don't have one yet so they can be
// created inline). Mirrors the legacy Component_ResolutionFunctionEditor, but prop-driven for WorkHub
// (no ModuleFE_PathwayEditor singleton, no PathwayManagerFunctions) so it's multi-tab safe.
// Manual save (per-card + global) — the parent-detach confirmation only fires on Save.
export class Panel_PathwayResolutionAccordion extends ComponentSync {
    // ######################## Life Cycle ########################
    // Only re-derive for cache events that touch this state's transitions / resolution functions,
    // so unrelated updates don't clobber in-progress edits (mirrors Renderer_PathwayTransitions).
    __onPathwayTransitionUpdated(...params) {
        if (!this.mounted)
            return;
        const changed = asArray(params[1]);
        const changedIds = changed.map(dbObjectToId);
        // Also catch brand-new transitions for this state (e.g. a just-duplicated one) whose id isn't in
        // transitionIds yet, so the new card shows up without waiting for a full resync.
        if (arrayIncludesAny(changedIds, this.state.transitionIds) || changed.some(t => t.sourceId === this.state.stateId))
            this.reDeriveState();
    }
    __onExpressionUpdated(...params) {
        if (!this.mounted)
            return;
        const changedIds = asArray(params[1]).map(dbObjectToId);
        const myResolutionFunctionIds = filterInstances(this.state.transitionIds.map(id => ModuleFE_PathwayTransition.cache.unique(id)?.resolutionFunctionId));
        if (arrayIncludesAny(changedIds, myResolutionFunctionIds))
            this.reDeriveState();
    }
    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        this.updateDirty();
    }
    componentDidUpdate() {
        this.updateDirty();
    }
    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        setResolutionAccordionDirty(this.props.tabId, false);
    }
    handleBeforeUnload = (e) => {
        if (!isResolutionAccordionDirty(this.props.tabId))
            return;
        e.preventDefault();
        e.returnValue = '';
    };
    deriveStateFromProps(nextProps, state) {
        const stateChanged = state.stateId !== nextProps.stateId || state.pathwayId !== nextProps.pathwayId;
        state.stateId = nextProps.stateId;
        state.pathwayId = nextProps.pathwayId;
        const transitions = sortArray(ModuleFE_PathwayTransition.cache.filter(t => t.sourceId === nextProps.stateId && t.pathwayIds.includes(nextProps.pathwayId)), t => t.priorityIndex);
        const transitionIds = transitions.map(dbObjectToId);
        state.transitionIds = transitionIds;
        const prevMap = stateChanged ? undefined : state.mapTransitionIdToEditable;
        state.mapTransitionIdToEditable = this.buildEditableMap(transitionIds, prevMap);
        if (stateChanged || !state.collapsedMap)
            state.collapsedMap = reduceToMap(transitionIds, id => id, () => true);
        else
            transitionIds.forEach(id => {
                if (!(id in state.collapsedMap))
                    state.collapsedMap[id] = true;
            });
        // Keep a valid focused item for the side panel (default to the first transition).
        if (!state.focusedTransitionId || !transitionIds.includes(state.focusedTransitionId))
            state.focusedTransitionId = transitionIds[0];
        return state;
    }
    // ######################## Logic ########################
    // Reuse existing editables (preserve in-progress edits) and only build new ones for new transitions.
    buildEditableMap(transitionIds, prevMap) {
        return reduceToMap(transitionIds, id => id, transitionId => {
            if (prevMap?.[transitionId])
                return prevMap[transitionId];
            const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
            const expression = ModuleFE_Expression.cache.unique(transition?.resolutionFunctionId);
            // A linked resolution function that isn't in cache means an inconsistent state; surface it
            // (the card falls back to an empty, creatable RF rather than navigating away like the old panel).
            if (exists(transition?.resolutionFunctionId) && !expression)
                this.logWarning(`resolution function ${transition.resolutionFunctionId} not found in cache for transition ${transitionId}`);
            return this.createEditableItem(transition, expression);
        });
    }
    createEditableItem(transition, expression) {
        if (!transition || !transition._id)
            throw new MUSTNeverHappenException('cannot create a resolution function without a transition');
        const _transition = transition;
        const editable = new EditableDBItemV3(expression ?? getEmptyResolutionFunction(_transition), ModuleFE_Expression)
            .setAutoSave(false)
            .setSaveAction(item => this.saveResolutionFunction(_transition, item))
            .setOnDelete(item => this.deleteResolutionFunction(_transition, item));
        // Validate BEFORE wiring onChanged: this can run inside ComponentBase's constructor (via
        // deriveStateFromProps), where class-field arrows aren't initialized yet. validate() fires onChanged
        // only on failure, so wiring it afterwards keeps that path a no-op at construction time while still
        // storing the validation results on the editable. Mirrors the legacy Component_ResolutionFunctionEditor.
        if (editable.get('_id'))
            editable.validate();
        editable.setOnChanged(async () => this.onEditableChanged());
        return editable;
    }
    onEditableChanged = () => {
        this.updateDirty();
        this.forceUpdate();
    };
    updateDirty = () => {
        const dirty = _values(this.state.mapTransitionIdToEditable).some(editable => editable.hasChanges());
        setResolutionAccordionDirty(this.props.tabId, dirty);
    };
    // Pathway-aware save (mirrors the legacy resolution editor) but keyed by the card's transition so it also
    // works for brand-new resolution functions (no _id yet) — the backend creates + links the expression.
    saveResolutionFunction = async (transition, item) => {
        const pathway = ModuleFE_Pathway.cache.unique(this.props.pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`cannot find pathway with id ${this.props.pathwayId}`);
        // Editing a resolution function inherited from a parent pathway detaches it (and its transition) from the parent.
        if (pathway._parentId && transition.pathwayIds?.includes(pathway._parentId))
            return new Promise(resolve => {
                Dialog_Confirmation.show({
                    content: () => _jsx("div", { children: "By editing this resolution function, it'll detach it and its transition from the parent and it won't receive more updates" }),
                    buttons: [
                        {
                            label: 'Yes, Edit',
                            isAsync: true,
                            action: async () => {
                                const result = await ModuleFE_PathwayTransition._v1.detachTransitionContext({
                                    resolutionFunction: item,
                                    pathwayTransition: transition,
                                    pathwayId: pathway._id
                                }).executeSync();
                                resolve(result.resolutionFunctions.updated[0]);
                            }
                        },
                        {
                            label: 'Cancel',
                            isPreferred: true,
                            action: () => resolve(item)
                        }
                    ]
                });
            });
        const { resolutionFunction } = await ModuleFE_PathwayTransition._v1.updateResolutionFunction({
            transitionId: transition._id,
            resolutionFunction: item
        }).executeSync();
        return resolutionFunction;
    };
    deleteResolutionFunction = async (transition, item) => {
        if (item._id) {
            await ModuleFE_PathwayTransition._v1.deleteResolutionFunction({ transitionId: transition._id }).executeSync();
            ModuleFE_Toaster.toastSuccess('Resolution function deleted');
        }
        // Reset the card to an empty (creatable) resolution function for the same transition (immutably).
        this.setState({
            mapTransitionIdToEditable: {
                ...this.state.mapTransitionIdToEditable,
                [transition._id]: this.createEditableItem(transition)
            }
        }, this.updateDirty);
    };
    onCollapse = (transitionId) => {
        const willExpand = this.state.collapsedMap[transitionId];
        // Immutable update (new map reference) so the toggle reliably re-renders the card —
        // mirrors collapseExpandAll, which works, unlike in-place mutation + forceUpdate.
        const collapsedMap = { ...this.state.collapsedMap, [transitionId]: !willExpand };
        // Expanding a card also focuses it in the side panel.
        this.setState(willExpand ? { collapsedMap, focusedTransitionId: transitionId } : { collapsedMap });
    };
    collapseExpandAll = () => {
        const shouldCollapse = _values(this.state.collapsedMap).some(value => !value);
        this.setState({ collapsedMap: reduceToMap(_keys(this.state.collapsedMap), id => id, () => shouldCollapse) });
    };
    // Drag-to-reorder: renumber priorityIndex by position (1..N) and persist only the transitions that changed.
    // Frontend-only — v1.upsertAll writes the full objects in one transaction (set.all); onEntriesUpdated then
    // refreshes the cache and dispatches __onPathwayTransitionUpdated → reDeriveState re-sorts by the new
    // priorityIndex, which already matches the optimistic order below (no snap-back / flicker).
    onReorder = async (orderedTransitionIds) => {
        const toPersist = filterInstances(orderedTransitionIds.map((transitionId, i) => {
            const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
            const newIndex = i + 1;
            if (!transition || transition.priorityIndex === newIndex)
                return undefined;
            return { ...transition, priorityIndex: newIndex };
        }));
        // Optimistic UI: paint the new order immediately so the drop feels instant.
        this.setState({ transitionIds: orderedTransitionIds });
        if (!toPersist.length)
            return;
        try {
            await ModuleFE_PathwayTransition.v1.upsertAll(toPersist).executeSync();
        }
        catch (err) {
            this.logError('Failed to reorder resolution functions', err);
            ModuleFE_Toaster.toastError('Failed to reorder resolution functions');
            // Revert to the persisted (cache) order.
            this.reDeriveState();
        }
    };
    // Save sequentially (not Promise.all): inherited resolution functions each pop their own detach-from-parent
    // confirmation dialog, so parallel saves would stack overlapping modals. Sequential also lets a single
    // failure be reported without aborting the rest.
    saveAll = async () => {
        for (const editable of _values(this.state.mapTransitionIdToEditable)) {
            if (!editable.hasChanges())
                continue;
            try {
                await editable.save();
            }
            catch (err) {
                this.logError('Failed saving resolution function', err);
                ModuleFE_Toaster.toastError('Failed saving a resolution function');
            }
        }
        this.updateDirty();
    };
    back = async () => {
        if (!(await guardResolutionAccordionUnsaved(this.props.tabId)))
            return;
        this.props.workHubItem.updateArgs(this.props.tabId, { editMode: false });
    };
    // Duplicate the whole transition (transition + its resolution function) into the same state via copyTransition
    // (source/target = the original's). The clone inherits the original's priorityIndex and is expanded + focused so
    // it's ready to edit. The copyTransition caller primes the transition + RF caches so the card shows in full.
    duplicateTransition = async (transition) => {
        const editable = this.state.mapTransitionIdToEditable[transition._id];
        // The clone is built from the persisted transition/RF, so unsaved edits on the source card wouldn't be
        // included. Offer to save them first, otherwise the user would silently duplicate stale content.
        if (editable?.hasChanges()) {
            const save = await new Promise(resolve => {
                Dialog_Confirmation.show({
                    header: 'Unsaved Changes',
                    content: () => _jsx("div", { children: "You have unsaved changes in this transition. Save them before duplicating?" }),
                    buttons: [
                        { label: 'Cancel', action: () => resolve(false) },
                        { label: 'Save & Duplicate', isPreferred: true, action: () => resolve(true) },
                    ]
                });
            });
            if (!save)
                return;
            try {
                await editable.save();
            }
            catch (err) {
                this.logError('Failed saving resolution function before duplicate', err);
                return ModuleFE_Toaster.toastError('Failed saving the transition');
            }
        }
        // Re-read the (possibly just-saved) transition from cache so the copy reflects the persisted state.
        const source = ModuleFE_PathwayTransition.cache.unique(transition._id) ?? transition;
        try {
            const { pathwayTransition } = await ModuleFE_PathwayTransition._v1.copyTransition({
                pathwayTransitionToCopy: { ...source, label: `${source.label ?? 'transition'} copy` },
                sourceStateId: source.sourceId,
                targetStateId: source.targetId,
            }).executeSync();
            this.setState({
                focusedTransitionId: pathwayTransition._id,
                collapsedMap: { ...this.state.collapsedMap, [pathwayTransition._id]: false },
            });
            ModuleFE_Toaster.toastSuccess(`Transition '${source.label ?? 'transition'}' duplicated`);
        }
        catch (err) {
            this.logError('Failed duplicating transition', err);
            ModuleFE_Toaster.toastError('Failed duplicating transition');
        }
    };
    // Delete the whole transition (not just its resolution function) via the shared confirmation dialog. On delete
    // the transition leaves the cache → __onPathwayTransitionUpdated re-derives and the card disappears; focus
    // falls back to the first transition in deriveStateFromProps.
    deleteTransition = (transition) => {
        Dialog_DeletePathwayTransition.show({
            pathwayId: this.props.pathwayId,
            transitionId: transition._id,
        });
    };
    // ######################## Render ########################
    render() {
        const focusedEditable = this.state.focusedTransitionId
            ? this.state.mapTransitionIdToEditable[this.state.focusedTransitionId]
            : undefined;
        return _jsxs(LL_H_C, { className: 'pathway-resolution-accordion', children: [_jsxs(LL_V_L, { className: 'pathway-resolution-accordion__main', children: [this.renderHeader(), _jsx(LL_V_L, { className: 'pathway-resolution-accordion__list', children: _jsx(TS_ListOrganizer, { 
                                // Fresh array reference each render (like Component_TermsAndExpression): TS_ListOrganizer's
                                // shouldComponentUpdate is state-only, so with a stable reference it wouldn't re-render the
                                // cards when collapsedMap / focus changes → the accordion wouldn't open. During a drag the
                                // Panel doesn't re-render, so this doesn't reset the in-progress order.
                                items: [...this.state.transitionIds], renderer: this.renderCard, onOrderChanged: this.onReorder }) })] }), focusedEditable && _jsx(Panel_ExpressionSidePanel, { editable: focusedEditable })] });
    }
    renderHeader = () => {
        const state = ModuleFE_PathwayState.cache.unique(this.props.stateId);
        const anyExpanded = _values(this.state.collapsedMap).some(value => !value);
        const expandClassName = _className('pathway-resolution-accordion__expand-all', anyExpanded && 'expanded');
        return _jsxs(LL_V_L, { className: 'pathway-resolution-accordion__header', children: [_jsx("div", { className: 'pathway-resolution-accordion__back', onClick: this.back, children: '‹ Back to map' }), _jsxs(LL_H_C, { className: 'pathway-resolution-accordion__header-main', children: [_jsxs(LL_V_L, { className: 'pathway-resolution-accordion__title-area', children: [_jsxs("div", { className: 'pathway-resolution-accordion__title', children: [state?.name ?? 'N/A', " \u2014 Resolution Functions"] }), _jsx("div", { className: 'pathway-resolution-accordion__subtitle', children: "State" })] }), _jsxs(LL_H_C, { className: 'pathway-resolution-accordion__actions', children: [_jsx(ICONS.advisorCollapseAll.component, { className: expandClassName, onClick: this.collapseExpandAll }), _jsx(Button, { variant: 'primary', className: 'preferred-action', onClick: this.saveAll, children: "Save" })] })] })] });
    };
    focusCard = (transitionId) => {
        if (this.state.focusedTransitionId !== transitionId)
            this.setState({ focusedTransitionId: transitionId });
    };
    renderCard = (props) => {
        const transitionId = props.item;
        const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
        if (!transition)
            throw new MUSTNeverHappenException(`cannot find transition with id ${transitionId}`);
        const editable = this.state.mapTransitionIdToEditable[transitionId];
        if (!editable)
            return null;
        const collapsed = this.state.collapsedMap[transitionId];
        const focused = this.state.focusedTransitionId === transitionId;
        // Clicking anywhere on the card focuses it (updates the side panel / Pretty Print) without expanding;
        // the arrow inside still toggles collapse. Mirrors the legacy click-to-preview + highlight behavior.
        // The wrapper is the drop target; only the drag-handle icon inside the card is draggable (so the
        // collapse arrow and click-to-focus keep working).
        return _jsx("div", { className: _className('pathway-resolution-accordion__card', focused && 'focused', props.dragged && 'dragging'), onClick: () => this.focusCard(transitionId), onDragOver: e => props.onDragOver(e, props.index), onDragLeave: e => props.onDragLeave(e, props.index), children: _jsx(Component_ResolutionFunctionCard, { className: collapsed ? 'collapsed' : undefined, index: transition.priorityIndex, collapsed: collapsed, onCollapse: this.onCollapse, transitionId: transitionId, editable: editable, hideDuplicateAction: true, onDuplicateTransition: () => this.duplicateTransition(transition), onDeleteTransition: () => this.deleteTransition(transition), onDragStart: e => {
                    // Drag image = the whole card (not the tiny handle). setData is required or Chromium
                    // cancels the drag on start.
                    const container = e.currentTarget.closest('.resolution-function-card') ?? e.currentTarget;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/html', container);
                    e.dataTransfer.setDragImage(container, 30, 30);
                    props.onDragStart(e, props.index);
                }, onDragEnd: props.onDragEnd }) }, `rf-${transitionId}`);
    };
}
//# sourceMappingURL=Panel_PathwayResolutionAccordion.js.map